// src/pages/Employees/ReturnActModal.jsx
import React, { useState, useEffect } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import {
  Modal, Form, Select, DatePicker, Table, Checkbox,
  Space, Tag, Typography, Alert, message, Spin, Input, Tooltip
} from 'antd';
import { RollbackOutlined, EditOutlined, CheckOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { returnActsAPI } from '../../api/client';

const { Text } = Typography;

const RU_TO_SR_PREFIX = {
  "Системный блок":          "Računar",
  "Моноблок":                "All-in-one računar",
  "Игровой ноутбук":         "Gaming laptop",
  "Ноутбук":                 "Laptop",
  "Монитор":                 "Monitor",
  "Телевизор":               "Televizor",
  "Клавиатура":              "Tastatura",
  "Беспроводная клавиатура": "Bežična tastatura",
  "Мышь":                    "Miš",
  "Беспроводная мышь":       "Bežični miš",
  "Коврик для мыши":         "Podloga za miš",
  "Наушники":                "Slušalice",
  "Гарнитура":               "Slušalice sa mikrofonom",
  "Беспроводные наушники":   "Bežične slušalice",
  "Колонки":                 "Zvučnici",
  "Акустика":                "Zvučnici",
  "Веб-камера":              "Veb-kamera",
  "Камера":                  "Kamera",
  "Микрофон":                "Mikrofon",
  "Роутер":                  "Ruter",
  "Маршрутизатор":           "Ruter",
  "Коммутатор":              "Svič",
  "Принтер":                 "Štampač",
  "МФУ":                     "Višefunkcijski uređaj",
  "Сканер":                  "Skener",
  "Проектор":                "Projektor",
  "Планшет":                 "Tablet",
  "Телефон":                 "Telefon",
  "Смартфон":                "Smartphone",
  "ИБП":                     "UPS",
};

function autoTranslate(name) {
  if (!name) return name;
  const entries = Object.entries(RU_TO_SR_PREFIX).sort((a, b) => b[0].length - a[0].length);
  for (const [ru, sr] of entries) {
    if (name.startsWith(ru)) {
      const rest = name.slice(ru.length);
      return sr + (rest && !rest.startsWith(' ') ? ' ' + rest : rest);
    }
  }
  return name;
}

export default function ReturnActModal({ open, onClose, employee, equipment }) {
  const [form] = Form.useForm();
  const [selectedEquipment, setSelectedEquipment] = useState([]);
  const [companyId, setCompanyId]   = useState(null);
  const [language, setLanguage]     = useState('ru');
  const [nameOverrides, setNameOverrides] = useState({});
  const [editingId, setEditingId]   = useState(null);
  const [editingValue, setEditingValue] = useState('');

  useEffect(() => {
    if (open && equipment?.length > 0) {
      setSelectedEquipment(equipment.map(e => e.id));
    }
    if (open && employee?.companies?.length > 0) {
      const active = employee.companies.find(c => c.is_active);
      if (active) {
        setCompanyId(active.company_id);
        form.setFieldsValue({ company_id: active.company_id });
        const lang = active.company_name?.toLowerCase().includes('inspired') ? 'sr' : 'ru';
        setLanguage(lang);
      }
    }
    form.setFieldsValue({ act_date: dayjs() });
    setNameOverrides({});
    setEditingId(null);
  }, [open, employee, equipment]);

  const companies = (employee?.companies || [])
    .filter(c => c.is_active)
    .map(c => ({ label: c.company_name, value: c.company_id }));

  const { data: signers, isLoading: signersLoading } = useQuery({
    queryKey: ['signers', companyId],
    queryFn:  () => returnActsAPI.getSigners(companyId).then(r => r.data),
    enabled:  !!companyId,
  });

  const generateMutation = useMutation({
    mutationFn: (data) => returnActsAPI.generate(data),
    onSuccess: (res) => {
      const result = res.data;
      message.success(`Акт возврата ${result.act_number} сформирован — ${result.items} позиций`);
      if (result.download_url) {
        window.open(result.download_url, '_blank');
      }
      onClose();
      form.resetFields();
      setSelectedEquipment([]);
      setNameOverrides({});
    },
    onError: (e) => message.error(e?.response?.data?.detail || 'Ошибка формирования акта'),
  });

  const handleSubmit = () => {
    form.validateFields().then(values => {
      if (selectedEquipment.length === 0) {
        message.warning('Выберите хотя бы одну позицию оборудования');
        return;
      }
      generateMutation.mutate({
        employee_id:    employee.id,
        company_id:     values.company_id,
        signer_id:      values.signer_id,
        act_date:       values.act_date.format('YYYY-MM-DD'),
        equipment_ids:  selectedEquipment,
        language,
        name_overrides: Object.keys(nameOverrides).length > 0 ? nameOverrides : null,
      });
    });
  };

  const startEdit = (record) => {
    const current = nameOverrides[record.id] ?? autoTranslate(record.name);
    setEditingId(record.id);
    setEditingValue(current);
  };

  const commitEdit = (id) => {
    if (editingValue.trim()) {
      setNameOverrides(prev => ({ ...prev, [id]: editingValue.trim() }));
    }
    setEditingId(null);
  };

  const isSr = language === 'sr';

  const equipmentColumns = [
    {
      title: '', key: 'check', width: 40,
      render: (_, record) => (
        <Checkbox
          checked={selectedEquipment.includes(record.id)}
          onChange={(e) => {
            if (e.target.checked) {
              setSelectedEquipment(prev => [...prev, record.id]);
            } else {
              setSelectedEquipment(prev => prev.filter(id => id !== record.id));
            }
          }}
        />
      ),
    },
    {
      title: 'Инв. №', dataIndex: 'inventory_number', width: 100,
      render: (v) => <Text code style={{ fontSize: 11 }}>{v}</Text>,
    },
    { title: 'Тип', dataIndex: 'type', width: 110, ellipsis: true },
    {
      title: 'Наименование', dataIndex: 'name', ellipsis: true,
      render: (v) => <Text type="secondary" style={{ fontSize: 11 }}>{v}</Text>,
    },
    ...(isSr ? [{
      title: (
        <Space size={4}>
          <span>Для акта</span>
          <Tag color="purple" style={{ fontSize: 10, margin: 0 }}>SR</Tag>
        </Space>
      ),
      key: 'name_sr',
      render: (_, record) => {
        const translated = nameOverrides[record.id] ?? autoTranslate(record.name);
        const isOverridden = !!nameOverrides[record.id];
        const isAuto = translated !== record.name && !isOverridden;

        if (editingId === record.id) {
          return (
            <Space>
              <Input
                size="small"
                value={editingValue}
                onChange={e => setEditingValue(e.target.value)}
                onPressEnter={() => commitEdit(record.id)}
                onBlur={() => commitEdit(record.id)}
                style={{ width: 220 }}
                autoFocus
              />
              <CheckOutlined
                style={{ color: '#52c41a', cursor: 'pointer' }}
                onClick={() => commitEdit(record.id)}
              />
            </Space>
          );
        }

        return (
          <Space size={4}>
            <Text style={{ fontSize: 12 }}>{translated}</Text>
            {isOverridden && (
              <Tooltip title="Отредактировано вручную">
                <Tag color="blue" style={{ fontSize: 10, margin: 0 }}>✎</Tag>
              </Tooltip>
            )}
            {isAuto && !isOverridden && (
              <Tooltip title="Переведено автоматически">
                <Tag color="purple" style={{ fontSize: 10, margin: 0 }}>авто</Tag>
              </Tooltip>
            )}
            <EditOutlined
              style={{ color: '#aaa', cursor: 'pointer', fontSize: 11 }}
              onClick={() => startEdit(record)}
            />
          </Space>
        );
      },
    }] : []),
  ];

  const needsCompanyChoice = companies.length > 1;

  return (
    <Modal
      open={open}
      title={
        <Space>
          <RollbackOutlined />
          <span>Акт возврата — {employee?.full_name}</span>
        </Space>
      }
      onOk={handleSubmit}
      onCancel={() => { onClose(); form.resetFields(); setSelectedEquipment([]); setNameOverrides({}); }}
      confirmLoading={generateMutation.isPending}
      okText="Сформировать и скачать"
      cancelText="Отмена"
      width={isSr ? 820 : 680}
    >
      <Form form={form} layout="vertical" style={{ marginTop: 16 }}>

        {needsCompanyChoice && (
          <Alert
            type="info"
            style={{ marginBottom: 16 }}
            message="У сотрудника несколько юр. лиц — выберите от кого формировать акт"
            showIcon
          />
        )}

        <Form.Item
          name="company_id"
          label="Юридическое лицо"
          rules={[{ required: true, message: 'Выберите юр. лицо' }]}
        >
          <Select
            options={companies}
            onChange={(v, opt) => {
              setCompanyId(v);
              form.setFieldValue('signer_id', undefined);
              const lang = opt?.label?.toLowerCase().includes('inspired') ? 'sr' : 'ru';
              setLanguage(lang);
              setNameOverrides({});
            }}
          />
        </Form.Item>

        <Form.Item
          name="signer_id"
          label="Подписант"
          rules={[{ required: true, message: 'Выберите подписанта' }]}
        >
          {signersLoading ? <Spin size="small" /> : (
            <Select
              placeholder="Выберите кто подписывает акт"
              options={(signers || []).map(s => ({
                label: (
                  <div>
                    <div>{s.name_nominative}</div>
                    <div style={{ fontSize: 11, color: '#888' }}>{s.basis}</div>
                  </div>
                ),
                value: s.id,
              }))}
            />
          )}
        </Form.Item>

        <Form.Item
          name="act_date"
          label="Дата акта"
          rules={[{ required: true, message: 'Укажите дату' }]}
        >
          <DatePicker format="DD.MM.YYYY" style={{ width: '100%' }} />
        </Form.Item>

        <Form.Item label={`Оборудование (выбрано ${selectedEquipment.length} из ${equipment?.length || 0})`}>
          {isSr && (
            <Alert
              type="info"
              showIcon
              style={{ marginBottom: 8 }}
              message="Наименования переведены автоматически. Нажмите ✎ чтобы поправить любое."
            />
          )}
          <div style={{ marginBottom: 8 }}>
            <Space>
              <a onClick={() => setSelectedEquipment(equipment.map(e => e.id))}>Выбрать всё</a>
              <a onClick={() => setSelectedEquipment([])}>Снять выделение</a>
              {isSr && Object.keys(nameOverrides).length > 0 && (
                <a onClick={() => setNameOverrides({})} style={{ color: '#ff4d4f' }}>
                  Сбросить правки ({Object.keys(nameOverrides).length})
                </a>
              )}
            </Space>
          </div>
          <Table
            dataSource={equipment || []}
            columns={equipmentColumns}
            rowKey="id"
            size="small"
            pagination={false}
            scroll={{ y: 220 }}
          />
        </Form.Item>

      </Form>
    </Modal>
  );
}